/**
 * Section EPI ADT — Structure officielle Lafarge avec pictogrammes officiels
 * Images extraites depuis le Google Sheet "Pictogrammes" — /pictograms/*.png|jpg
 *
 * N : EPI Minimum obligatoires (affichage figé)
 * O : Autres obligations sur notre site (affichage figé)
 * P : Mesures génériques à appliquer systématiquement (affichage figé)
 * Q : EPI Spécifiques (sélectionnable par l'utilisateur)
 */
import { CheckCircle2 } from 'lucide-react';

const PICTO = (f) => `/pictograms/${f}`;

const N_MINIMUM = [
  { id: 'casque', label: 'Casque', src: PICTO('image46.png') },
  { id: 'lunettes', label: 'Lunettes', src: PICTO('image45.png') },
  { id: 'chaussures', label: 'Chaussures', src: PICTO('image17.png') },
  { id: 'gants', label: 'Gants 4.X.4.3.D', src: PICTO('image11.png') },
  { id: 'hv', label: 'Tenue HV', src: PICTO('image7.png') },
  { id: 'auditive', label: 'Protection auditive', src: PICTO('image19.png') },
];

const O_OBLIGATIONS = [
  { id: 'no_running', label: 'Pas de course', src: PICTO('image32.png') },
  { id: 'no_phone', label: 'Pas de téléphone', src: PICTO('image20.png') },
  { id: 'no_alcool', label: "Pas d'alcool", src: PICTO('image22.png') },
  { id: 'ceinture', label: 'Ceinture', src: PICTO('image1.png') },
  { id: 'attention_engins', label: 'Attention engins', src: PICTO('image13.jpg') },
  { id: '20kmh', label: '20 km/h', src: PICTO('image5.jpg') },
];

const P_MESURES = [
  "Ordre et propreté durant l'intervention et à la fin",
  'Vigilance météo / Luminosité / Fatigue & Déplacement',
  'Évacuation de vos déchets, et protections remises en place',
];

const Q_EPI = [
  {
    category: 'Protection des yeux & visage',
    items: [
      { id: 'q_lunettes_bavette', label: 'Lunettes bavette ou étanches', src: PICTO('image3.png') },
      { id: 'q_lunettes_teintees', label: 'Couvrantes teintées 5', src: PICTO('image15.png') },
      { id: 'q_pare_visage', label: 'Pare visage ajouté', src: PICTO('image18.png') },
      { id: 'q_visiere_elec', label: 'À visière électronique', src: PICTO('image16.png') },
    ],
  },
  {
    category: 'Protection respiratoire',
    items: [
      { id: 'q_ventile', label: 'Ventilé', src: PICTO('image23.jpg') },
      { id: 'q_masque_ap3', label: 'Masque type A & P3', src: PICTO('image21.png') },
    ],
  },
  {
    category: 'Protection du corps',
    items: [
      { id: 'q_tenue_multi', label: 'Tenue multirisques', src: PICTO('image10.png') },
      { id: 'q_tenue_its', label: 'Tenue ITS ringardage', src: PICTO('image2.png') },
      { id: 'q_tenue_soudeur', label: 'Tenue couvrante soudeur', src: PICTO('image9.png') },
    ],
  },
  {
    category: 'Protection des pieds soudeur',
    items: [
      { id: 'q_soudeur_guetres', label: 'Soudeur ou guêtres', src: PICTO('image14.png') },
      { id: 'q_haute_temp', label: 'Haute température', src: PICTO('image11.png') },
    ],
  },
  {
    category: 'Protection des mains et poignets soudeur',
    items: [
      { id: 'q_iso_11611', label: 'EN ISO 11611 Classe 2 A1', src: PICTO('image12.png') },
      { id: 'q_anticoupures', label: 'Anticoupures', src: PICTO('image8.png') },
    ],
  },
  {
    category: 'Protection des mains et corps - chimique',
    items: [
      { id: 'q_gants_nitrile', label: 'Gants nitrile / combinaison', src: PICTO('image6.jpg') },
      { id: 'q_masque_gaz', label: 'Masque à gaz', src: PICTO('image4.png') },
    ],
  },
];

export const Q_EPI_LIST = Q_EPI;

function Picto({ src, alt, size = 'normal' }) {
  const cls = size === 'small'
    ? 'w-10 h-10 object-contain'
    : 'w-14 h-14 object-contain';
  return (
    <img src={src} alt={alt} className={cls} loading="lazy"
      onError={(e) => { e.target.style.opacity = '0.3'; }}
    />
  );
}

export default function AdtEpiSection({ value, onChange }) {
  const v = value || {};
  const toggle = (id) => onChange({ ...v, [id]: !v[id] });

  return (
    <div className="space-y-4">
      {/* N - O - P */}
      <div className="grid md:grid-cols-3 gap-3">
        {/* N */}
        <div className="border border-stone-200">
          <div className="bg-blue-700 text-white px-3 py-2 text-[11px] font-bold uppercase tracking-wider">
            N — EPI Minimum obligatoires
          </div>
          <div className="p-3 grid grid-cols-3 gap-3">
            {N_MINIMUM.map((e) => (
              <div key={e.id} className="flex flex-col items-center text-center" data-testid={`epi-n-${e.id}`}>
                <Picto src={e.src} alt={e.label} />
                <span className="text-[10px] font-semibold mt-1 leading-tight">{e.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* O */}
        <div className="border border-stone-200">
          <div className="bg-amber-500 text-white px-3 py-2 text-[11px] font-bold uppercase tracking-wider">
            O — Autres obligations sur notre site
          </div>
          <div className="p-3 grid grid-cols-3 gap-3">
            {O_OBLIGATIONS.map((e) => (
              <div key={e.id} className="flex flex-col items-center text-center" data-testid={`epi-o-${e.id}`}>
                <Picto src={e.src} alt={e.label} />
                <span className="text-[10px] font-semibold mt-1 leading-tight">{e.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* P */}
        <div className="border border-stone-200">
          <div className="bg-rose-300 text-rose-900 px-3 py-2 text-[11px] font-bold uppercase tracking-wider">
            P — Mesures génériques à appliquer Systématiquement
          </div>
          <div className="p-3 space-y-3">
            {P_MESURES.map((m, i) => (
              <div key={i} className="flex items-start gap-2 text-xs leading-relaxed">
                <CheckCircle2 className="w-4 h-4 text-green-700 flex-shrink-0 mt-0.5" />
                <span className="text-stone-800">{m}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Q - selectable with pictograms */}
      <div className="border border-stone-200">
        <div className="bg-sky-100 text-sky-900 px-3 py-2 text-[11px] font-bold uppercase tracking-wider">
          Q — EPI Spécifiques (cliquer sur les pictogrammes applicables selon les risques)
        </div>
        <div className="p-3 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Q_EPI.map((cat) => (
            <div key={cat.category}>
              <div className="text-[11px] italic font-semibold text-stone-700 mb-2 border-b border-stone-200 pb-1">
                {cat.category}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {cat.items.map((it) => {
                  const selected = !!v[it.id];
                  return (
                    <button
                      type="button"
                      key={it.id}
                      data-testid={`epi-q-${it.id}`}
                      onClick={() => toggle(it.id)}
                      className={`flex flex-col items-center text-center p-2 border-2 transition-all ${
                        selected
                          ? 'border-[#DA291C] bg-red-50 ring-2 ring-red-200'
                          : 'border-stone-200 hover:border-stone-400 bg-white'
                      }`}
                    >
                      <Picto src={it.src} alt={it.label} size="small" />
                      <span className={`text-[10px] font-semibold mt-1 leading-tight ${selected ? 'text-[#DA291C]' : 'text-stone-800'}`}>
                        {it.label}
                      </span>
                      {selected && (
                        <CheckCircle2 className="w-3.5 h-3.5 text-[#DA291C] mt-1" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
